import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../contexts/AuthContext'
import { 
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  KeyIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

const Profile: React.FC = () => {
  const { user } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: '',
    department: ''
  })

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    // Mock save - will be replaced with real API call
    setIsEditing(false)
    // Show success message
  }

  const handleCancel = () => {
    setFormData({
      fullName: user?.fullName || '',
      email: user?.email || '',
      phone: '',
      department: ''
    })
    setIsEditing(false)
  }

  const getRoleBadge = (role: string) => {
    const badges = {
      admin: { class: 'bg-purple-100 text-purple-800', label: 'Administrator' },
      billing: { class: 'bg-vcns-accent bg-opacity-10 text-vcns-accent', label: 'Billing Contact' },
      user: { class: 'bg-gray-100 text-gray-800', label: 'End User' }
    }
    return badges[role as keyof typeof badges] || badges.user
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading profile...</p>
      </div>
    )
  }

  const roleBadge = getRoleBadge(user.role)

  return (
    <>
      <Helmet>
        <title>Profile - VCNS Support Portal</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">Personal Information</h2>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="btn-outline text-sm"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>

              <div className="px-6 py-4">
                {isEditing ? (
                  <form onSubmit={handleSave} className="space-y-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="form-input"
                        placeholder="+44 20 1234 5678"
                      />
                    </div>

                    <div>
                      <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        id="department"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className="form-input"
                        placeholder="e.g., IT, Finance, Marketing"
                      />
                    </div>

                    <div className="flex space-x-3">
                      <button type="submit" className="btn-primary">
                        Save Changes
                      </button>
                      <button 
                        type="button" 
                        onClick={handleCancel}
                        className="btn-outline"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <dl className="space-y-4">
                    <div className="flex items-center">
                      <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                        <dd className="text-sm text-gray-900">{user.fullName}</dd>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email Address</dt>
                        <dd className="text-sm text-gray-900">{user.email}</dd>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <PhoneIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
                        <dd className="text-sm text-gray-900">+44 20 1234 5678</dd>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Department</dt>
                        <dd className="text-sm text-gray-900">Information Technology</dd>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <KeyIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Role</dt>
                        <dd className="text-sm">
                          <span className={`status-badge ${roleBadge.class}`}>
                            {roleBadge.label}
                          </span>
                        </dd>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <ClockIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Last Login</dt>
                        <dd className="text-sm text-gray-900">
                          {user.lastLoginAt 
                            ? new Date(user.lastLoginAt).toLocaleDateString('en-GB', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'Current session'
                          }
                        </dd>
                      </div>
                    </div>
                  </dl>
                )}
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="space-y-6">
            {/* Security Settings */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Security</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <button className="w-full btn-outline text-left">
                  <div>
                    <div className="font-medium">Change Password</div>
                    <div className="text-xs text-gray-500 mt-1">Update your account password</div>
                  </div>
                </button>
                
                <button className="w-full btn-outline text-left">
                  <div>
                    <div className="font-medium">Enable Two-Factor Authentication</div>
                    <div className="text-xs text-gray-500 mt-1">Add an extra layer of security</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Preferences */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Preferences</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">Email Notifications</div>
                    <div className="text-xs text-gray-500">Receive updates about your tickets</div>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-vcns-accent focus:ring-vcns-accent border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">SMS Alerts</div>
                    <div className="text-xs text-gray-500">Get urgent updates via SMS</div>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-vcns-accent focus:ring-vcns-accent border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">Renewal Reminders</div>
                    <div className="text-xs text-gray-500">30-day subscription renewal alerts</div>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 text-vcns-accent focus:ring-vcns-accent border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Organization Info */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Organization</h3>
              </div>
              <div className="px-6 py-4">
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Tenant Domain</dt>
                    <dd className="text-sm text-gray-900">demo.company.com</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Account Manager</dt>
                    <dd className="text-sm text-gray-900">VCNS Support Team</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Support Level</dt>
                    <dd className="text-sm">
                      <span className="status-badge bg-green-100 text-green-800">Premium Support</span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Profile
